import LocalizedStrings from 'react-localization';

import { Levels, Levels as OrganizationTier } from '@/graphql';

const pricingTiers = new LocalizedStrings<Record<OrganizationTier, string>>({
  en: {
    [OrganizationTier.Basic]: 'Personal',
    [OrganizationTier.Starter]: 'Team',
    [OrganizationTier.Business]: 'Pro',
    [OrganizationTier.Blueprint]: 'Blueprint',
    [OrganizationTier.Startup]: 'Startup',
    // NOTE(hobbyist-tier-desc)
    [OrganizationTier.Hobbyist]: 'Startup',
  },
});

const links = new LocalizedStrings({
  en: {
    // General links
    basetenHome: 'https://www.baseten.co/',
    basetenChangeLog: 'https://www.baseten.co/changelog',
    basetenCustomers: 'https://www.baseten.co/customers',
    basetenStatus: 'https://status.baseten.co/',
    basetenTerms: 'https://www.baseten.co/terms-and-conditions',
    basetenPrivacy: 'https://www.baseten.co/privacy-policy',
    basetenCareers: 'https://jobs.ashbyhq.com/baseten',
    basetenX: 'https://x.com/basetenco',
    basetenContactUs: 'https://www.baseten.co/talk-to-us/',
    basetenSupportEmail: 'mailto:support@baseten.co',

    // Model library links
    basetenModelLibrary: 'https://www.baseten.co/library',
    basetenModelLibraryDeepSeek: 'https://www.baseten.co/library/deepseek-r1/',
    basetenModelLibraryLlama: 'https://www.baseten.co/library/llama-4-maverick/',
    basetenModelLibraryQwen: 'https://www.baseten.co/library/qwen-3-235b/',

    // Blog links
    basetenBlog: 'https://www.baseten.co/blog',
    basetenBlogGPUGuides: 'https://www.baseten.co/blog/category/gpu-guides/',

    // DOCS GENERAL LINKS
    basetenDocs: 'https://docs.baseten.co',
    basetenDocsExamples: 'https://docs.baseten.co/examples/overview',
    basetenDocsReference: 'https://docs.baseten.co/reference/overview',

    // DOCS INFERENCE LINKS
    basetenDocsCallYourModel: 'https://docs.baseten.co/inference/calling-your-model',
    basetenDocsAsyncInference: 'https://docs.baseten.co/inference/async',

    // DOCS DEVELOPMENT LINKS
    basetenDocsDevelopmentConcepts: 'https://docs.baseten.co/development/concepts',
    basetenDocsDevelopingAModelOverview: 'https://docs.baseten.co/development/model/overview',
    basetenDocsDeployingYourFirstModel:
      'https://docs.baseten.co/development/model/build-your-first-model',
    basetenDocsDevelopmentGrpc: 'https://docs.baseten.co/development/model/grpc',

    // DOCS DEVELOPMENT LINKS -- MODEL APIs
    basetenDocsModelAPIs: 'http://docs.baseten.co/development/model-apis/overview',
    basetenDocsModelAPIsToolCalling:
      'https://docs.baseten.co/development/model-apis/overview#tool-calling',

    // DOCS DEVELOPMENT LINKS -- CHAINS
    basetenDocsChains: 'https://docs.baseten.co/chains/getting-started',
    basetenDocsChainsOverview: 'https://docs.baseten.co/development/chain/overview',
    basetenDocsCallChainsAPIEndpoint:
      'https://docs.baseten.co/chains/guide#call-a-chains-api-endpoint',
    basetenDocsChainsYourFirstChain: 'https://docs.baseten.co/development/chain/getting-started',
    basetenDocsChainsExampleRAG: 'https://docs.baseten.co/examples/chains-build-rag',
    basetenDocsChainsExampleTranscribe:
      'https://docs.baseten.co/examples/chains-audio-transcription',

    // DOCS DEPLOYMENT LINKS
    basetenDocsLDeploymentConcepts: 'https://docs.baseten.co/deployment/concepts',
    basetenDocsEnvironments: 'https://docs.baseten.co/deployment/environments',
    basetenDocsPromotion:
      'https://docs.baseten.co/deployment/deployments#environments-%26-promotion',
    basetenDocsSecretManagement: 'https://docs.baseten.co/observability/secrets',
    basetenDocsAutoScaling: 'https://docs.baseten.co/deployment/autoscaling',
    basetenDocsDevelopmentDeployment:
      'https://docs.baseten.co/deployment/deployments#development-deployment',
    basetenDocsModelResources: 'https://docs.baseten.co/deployment/resources',
    basetenDocsInstanceReference:
      'https://docs.baseten.co/deployment/resources#instance-type-reference',
    basetenDocsGPUInstanceReference: 'https://docs.baseten.co/deployment/resources#gpu-instances',
    basetenDocsCPUInstanceReference:
      'https://docs.baseten.co/deployment/resources#cpu-only-instances',
    basetenDocsEnvsAndPromotion:
      'https://docs.baseten.co/deployment/deployments#environments-%26-promotion',
    basetenDocsCanaryDeployments:
      'https://docs.baseten.co/deployment/deployments#canary-deployments',

    // DOCS INFERENCE LINKS
    basetenDocsWebhookSecretManagement:
      'https://docs.baseten.co/inference/async#securing-async-inference',
    basetenDocsAsyncInferenceTimeInQueue:
      'https://docs.baseten.co/observability/metrics#time-in-async-queue',
    basetenDocsAsyncInferenceQueueSize:
      'https://docs.baseten.co/observability/metrics#async-queue-size',

    // DOCS TRAINING LINKS
    basetenDocsTrainingOverview: 'https://docs.baseten.co/training/overview',
    basetenDocsTrainingRunning: 'https://docs.baseten.co/training/concepts#running-a-trainingjob',

    // DOCS OBSERVABILITY LINKS
    basetenDocsBillingCredits: 'https://docs.baseten.co/observability/usage#credits',
    basetenDocsAPIKeys: 'https://docs.baseten.co/observability/api-keys',

    // Applications docs
    basetenDocsViews: 'https://baseten.gitbook.io/app-builder-docs/applications/views',
    basetenDocsComponents:
      'https://baseten.gitbook.io/app-builder-docs/applications/views/components',
    basetenDocsViewBindings:
      'https://baseten.gitbook.io/app-builder-docs/applications/views/bindings',
    basetenDocsVariableParameters:
      'https://baseten.gitbook.io/app-builder-docs/applications/views/bindings#parameters',
    basetenDocsEventHandlers:
      'https://baseten.gitbook.io/app-builder-docs/applications/views/event-handlers',
    basetenDocsWorklets: 'https://baseten.gitbook.io/app-builder-docs/applications/worklets',
    basetenDocsBlocks: 'https://baseten.gitbook.io/app-builder-docs/applications/worklets/blocks',
    basetenDocsFiles: 'https://baseten.gitbook.io/app-builder-docs/applications/files',
    basetenDocsConsole: 'https://baseten.gitbook.io/app-builder-docs/applications/files#console',
    basetenDocsCustomPythonEnvironment:
      'https://baseten.gitbook.io/app-builder-docs/applications/files/custom-python-environment',
    basetenDocsSystemPackages:
      'https://baseten.gitbook.io/app-builder-docs/applications/files/custom-python-environment#system-packages',
    basetenDocsGithubIntegration:
      'https://baseten.gitbook.io/app-builder-docs/settings/integrations#github',
    basetenDocsGithubImport:
      'https://baseten.gitbook.io/app-builder-docs/applications/files/import',

    // Data docs
    basetenDocsPostgres: 'https://baseten.gitbook.io/app-builder-docs/data/baseten-postgres',
    basetenDocsDataConnections: 'https://baseten.gitbook.io/app-builder-docs/data/data-connections',
    basetenDocsQueryBuilder: 'https://baseten.gitbook.io/app-builder-docs/data/query-builder',

    // Organization docs
    basetenDocsManagingMembers: 'https://docs.baseten.co/observability/access',

    // Truss docs
    trussDocsHome: 'https://docs.baseten.co/truss-reference/overview',
    trussDocsUsage: 'https://truss.baseten.co/usage',
    trussPypi: 'https://pypi.org/project/truss/',

    // Restrected environments
    restrictedEnvironmentsDocs: 'https://docs.baseten.co/observability/restricted-environments',
  },
});

function basetenDocsBlockReference(block: string) {
  return `${links.basetenDocsBlocks}#${block}`;
}

function basetenDocsComponentReference(component: string) {
  return `${links.basetenDocsComponents}#${component}`;
}

function basetenDocsBindingReference(binding: string) {
  return `${links.basetenDocsViewBindings}#${binding}`;
}

function getLocalizedPricingTierName(tier: Levels) {
  return pricingTiers[tier];
}

function getRequestResourcesLink(modelName: string) {
  return `https://www.baseten.co/talk-to-us/?${modelName}`;
}

export {
  pricingTiers,
  getLocalizedPricingTierName,
  links,
  basetenDocsBlockReference,
  basetenDocsComponentReference,
  basetenDocsBindingReference,
  getRequestResourcesLink,
};
